export type Card = {
  id: string;
  title: string;
  url: string;
  imageUrl: string | null;
  imageProxyUrl: string | null;
  /** Показывается в витрине над названием; при отсутствии — домен магазина. */
  brand: string | null;
  price: number | null;
  currency: string | null;
  retailer: string | null;
  position: number;
};

export type CardDraft = {
  /** Локальный ключ для React — на бэкенде своего id ещё нет. */
  key: string;
  url: string;
  title: string;
  imageUrl: string | null;
  imageProxyUrl: string | null;
  brand: string | null;
  price: number | null;
  currency: string | null;
  retailer: string | null;
  parsedBy?: string;
  missing: string[];
};

/** Фото или видео, загруженные блогером для поста. */
export type PostMediaItem = {
  id: string;
  kind: 'photo' | 'video';
  url: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  position: number;
};

/**
 * Форма поста в канале. И та, и другая — одно сообщение с кнопкой:
 *   text — текст без медиа
 *   rich — медиа и текст вместе, отправляется sendRichMessage
 */
// Чем именно уйдёт пост. 'rich' — сообщение-статья: его не показывают
// клиенты старее июня 2026, поэтому по умолчанию оно выключено.
export type PostShape = 'text' | 'single' | 'album' | 'album+text' | 'rich';

/**
 * Раскладка медиа в посте:
 *   collage   — сеткой; её рисует сам Telegram, и вертикальное видео он
 *               растягивает на всю колонку, оставляя пустоту рядом
 *   slideshow — листаемый слайдер, каждый файл виден целиком
 */
export type MediaLayout = 'collage' | 'slideshow';

export type PostLimits = {
  maxCards: number;
  maxMedia: number;
  maxTextLength: number;
  maxPhotoMb: number;
  maxVideoMb: number;
};

export const DEFAULT_LIMITS: PostLimits = {
  maxCards: 5,
  maxMedia: 10,
  maxTextLength: 4096,
  maxPhotoMb: 10,
  maxVideoMb: 50,
};

export type Collection = {
  id: string;
  /** Текст поста в Telegram-HTML, уже очищенный сервером. */
  caption: string;
  /** Свой текст кнопки под постом; null — берётся значение по умолчанию. */
  buttonText: string | null;
  /** Как Telegram раскладывает медиа: сеткой или листаемым слайдером. */
  mediaLayout: MediaLayout;
  captionPreview?: string;
  status: 'draft' | 'published' | string;
  createdAt: string;
  updatedAt?: string;
  cards: Card[];
  media: PostMediaItem[];
  deepLink: string;
  /** Канал, в который опубликована подборка. Именно он подписывает витрину. */
  channel: { title: string | null; username: string | null } | null;
  isOwner: boolean;
};

export type CollectionSummary = {
  id: string;
  caption: string;
  captionPreview: string;
  status: string;
  createdAt: string;
  publishedAt: string | null;
  clicks: number;
  /** Открытия витрины. У постов старше счётчика — 0, и это не поломка. */
  views: number;
  cardsCount: number;
  mediaCount: number;
  cover: Card | null;
  coverMedia: PostMediaItem | null;
  deepLink: string;
};

/**
 * Период статистики. Отсчитывается ОТ ПУБЛИКАЦИИ поста, а не от сегодня:
 * пост живёт три-четыре дня, и «за последние сутки» на месячном посте
 * показывало бы честный, но бесполезный ноль.
 */
export type StatsWindow = 'day' | '3days' | 'week' | 'all';

export const STATS_WINDOW_LABELS: Record<StatsWindow, string> = {
  day: 'Первые сутки',
  '3days': '3 дня',
  week: 'Неделя',
  all: 'Всё время',
};

export type CardStat = {
  id: string;
  title: string;
  brand: string | null;
  clicks: number;
  /** Доля от всех переходов подборки, 0..1. */
  share: number;
};

/** Сутки от публикации: day 0 — первые 24 часа. */
export type DayStat = { day: number; views: number; clicks: number };

/**
 * Подписан ли человек на канал в момент открытия. unknown — спросить не
 * удалось: подборка не опубликована, бот не админ канала или Telegram молчал.
 * Это именно «не знаем», а не «не подписан».
 */
export type MemberStatus = 'member' | 'guest' | 'unknown';

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  member: 'Подписчики',
  guest: 'Не подписаны',
  unknown: 'Неизвестно',
};

export type AudienceStat = {
  status: MemberStatus;
  /** Открытий с этим статусом. */
  views: number;
  /** Разных людей. */
  people: number;
  /** Переходов, сделанных этими людьми. */
  clicks: number;
  /** Доля людей сегмента, дошедших до перехода, 0..1. */
  conversion: number;
};

export type CollectionStats = {
  window: StatsWindow;
  startedAt: string;
  publishedAt: string | null;
  views: { total: number; unique: number };
  clicks: { total: number; unique: number };
  /** Переходов на открытие. null — открытий не было, делить не на что. */
  ctr: number | null;
  /** Доля открывших, дошедших до перехода, 0..1. */
  conversion: number;
  /** Подписчики, случайные люди и те, про кого спросить не удалось. */
  audience: AudienceStat[];
  cards: CardStat[];
  daily: DayStat[];
  truncated: boolean;
};

export type StatsResponse = {
  collection: {
    id: string;
    captionPreview: string;
    status: string;
    createdAt: string;
    publishedAt: string | null;
  };
  stats: CollectionStats;
  /** С какого момента вообще считаются открытия. null — ещё ни одного. */
  viewsSince: string | null;
};

/** Канал автора. Их может быть несколько — например тестовый и основной. */
export type Channel = {
  id: number;
  chatId: string;
  title: string | null;
  username: string | null;
  isDefault: boolean;
};

export type Me = {
  user: { id: number; firstName: string; username: string | null; photoUrl: string | null };
  channels: Channel[];
  /** Разрешено ли создавать и публиковать подборки. */
  isAuthor: boolean;
  startParam: string | null;
  /** Приходят с сервера: у новой подборки взять их больше неоткуда. */
  limits: PostLimits;
};

export type ParseResult =
  | {
      ok: true;
      url: string;
      card: Omit<CardDraft, 'key' | 'missing'>;
      missing: string[];
    }
  | { ok: false; url: string; error: string };
